import { BOTONES } from '../../../componentes/botones';
import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';

import { HelperService } from '../../../auxiliares/servicios/helper.service';
import { ILayerMapaPersonalizado } from 'modelos/src';

export type TipoMapaBase = 'osm' | 'google' | 'satelite' | 'cartodb' | string;
export interface IMapaConfig {
  key: string;
  nombre: string;
  icono: string;
}

@Component({
  selector: 'app-selector-tipo-mapa',
  standalone: true,
  templateUrl: './selector-tipo-mapa.component.html',
  styleUrl: './selector-tipo-mapa.component.scss',
  imports: [BOTONES, ],
})
export class SelectorTipoMapaComponent implements OnInit, OnChanges {
  helper = inject(HelperService);

  @Output() tipoMapaBaseChange = new EventEmitter<TipoMapaBase>();

  //Mapas personalizados adicionales
  @Input() mapasPersonalizados: ILayerMapaPersonalizado[] = [];

  // Mostrar solo mapas específicos
  @Input() mapasMostrar?: TipoMapaBase[];

  // localStorage key para el mapa base que se está usando
  @Input() tipoMapaBaseKey?: string = 'tipoMapaBase';

  // Controlar si el componente usa posición absoluta o relativa
  @Input() useAbsolutePosition: boolean = true;

  // Color de fondo personalizable (usa CSS color-mix si se proporciona)
  @Input() backgroundColor?: string;

  // Mapas por defecto disponibles
  private readonly mapasDefecto: IMapaConfig[] = [
    {
      key: 'osm',
      nombre: 'Mapbox Standard',
      icono: 'assets/icons/osm-icon.svg',
    },
    {
      key: 'satelite',
      nombre: 'Vista Satelital',
      icono: 'assets/icons/satellite_view.png',
    },
    {
      key: 'cartodb',
      nombre: 'Minimal',
      icono: 'assets/icons/cartodb.webp',
    },
  ];

  //Lista completa de mapas disponibles
  public mapasDisponibles: IMapaConfig[] = [];

  // Selected base layer type
  @Input() tipoMapaBase: TipoMapaBase;

  constructor() {
    // No inicializar aquí, esperar a que llegue del padre o usar ngOnInit
    this.tipoMapaBase = 'osm';
  }

  ngOnInit(): void {
    // Si no viene del padre, obtener del localStorage
    if (!this.tipoMapaBase || this.tipoMapaBase === 'osm') {
      const mapaGuardado = this.obtenerMapaGuardado();
      if (mapaGuardado) {
        this.tipoMapaBase = mapaGuardado;
      }
    }
    this.inicializarMapasDisponibles();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Cuando cambia el input desde el padre, actualizar
    // IMPORTANTE: Aceptar TODOS los cambios, incluyendo el primero
    // El firstChange puede contener el valor correcto del localStorage/servicio
    if (changes['tipoMapaBase']) {
      const newValue = changes['tipoMapaBase'].currentValue;
      // Solo actualizar si el nuevo valor es diferente y no es undefined/null
      if (newValue && newValue !== this.tipoMapaBase) {
        this.tipoMapaBase = newValue;
      }
    }

    // Si cambian los mapas personalizados, re-inicializar
    if (changes['mapasPersonalizados']) {
      this.inicializarMapasDisponibles();
    }
  }

  private inicializarMapasDisponibles(): void {
    // Comenzar con mapas por defecto
    let mapas = [...this.mapasDefecto];

    // Filtrar mapas si se especifica cuáles mostrar
    if (this.mapasMostrar && this.mapasMostrar.length > 0) {
      mapas = mapas.filter((mapa) =>
        this.mapasMostrar!.includes(mapa.key as TipoMapaBase),
      );
    }

    // Agregar mapas personalizados
    if (this.mapasPersonalizados && this.mapasPersonalizados.length > 0) {
      const mapasPersonalizadosConfig: IMapaConfig[] =
        this.mapasPersonalizados.map((mapa, index) => ({
          key: `personalizado_${index}`,
          nombre: mapa.nombre || `Mapa Personalizado ${index + 1}`,
          icono: mapa.icono || 'assets/icons/default-map-icon.svg',
        }));

      mapas = [...mapas, ...mapasPersonalizadosConfig];
    }

    this.mapasDisponibles = mapas;

    // Validar que el mapa seleccionado esté disponible
    const mapaSeleccionadoDisponible = this.mapasDisponibles.find(
      (m) => m.key === this.tipoMapaBase,
    );

    // Si no existe, seleccionar OSM si está disponible; sino el primero
    if (!mapaSeleccionadoDisponible && this.mapasDisponibles.length > 0) {
      const osmDisponible = this.mapasDisponibles.find((m) => m.key === 'osm');
      this.tipoMapaBase = (osmDisponible?.key ||
        this.mapasDisponibles[0].key) as TipoMapaBase;
      this.guardarMapaSeleccionado();
    }
  }

  private obtenerMapaGuardado(): TipoMapaBase {
    const mapaGuardado = localStorage.getItem(this.tipoMapaBaseKey);
    return (mapaGuardado as TipoMapaBase) || 'osm';
  }

  private guardarMapaSeleccionado(): void {
    localStorage.setItem(this.tipoMapaBaseKey, this.tipoMapaBase);
  }

  public cambioDeMapa(tipo: TipoMapaBase): void {
    this.tipoMapaBase = tipo;
    this.guardarMapaSeleccionado();
    this.tipoMapaBaseChange.emit(tipo);
  }

  public obtenerMapaPersonalizadoPorKey(
    key: string,
  ): ILayerMapaPersonalizado | null {
    if (!key.startsWith('personalizado_')) return null;

    const index = parseInt(key.replace('personalizado_', ''));
    return this.mapasPersonalizados[index] || null;
  }
}
